import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";
import { useContent } from "@/context/ContentContext";

const easeOut = [0.19, 1, 0.22, 1];

export default function Home() {
    const [products, setProducts] = useState([]);
    const [edits, setEdits] = useState([]);
    const { get } = useContent();

    useEffect(() => {
        (async () => {
            try {
                const [p, e] = await Promise.all([
                    api.get("/products?limit=8"),
                    api.get("/products/edits"),
                ]);
                setProducts(p.data.items);
                setEdits(e.data.items);
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="home-hero">
                <div className="home-hero-copy">
                    <div className="eyebrow">{get("home.hero_eyebrow", "A curated resale destination · London")}</div>
                    <motion.h1
                        className="home-hero-title"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: easeOut }}
                    >
                        {get("home.hero_title_line1", "Current Season,")}
                        <br />
                        <em>{get("home.hero_title_line2", "In Circulation.")}</em>
                    </motion.h1>
                    <motion.p
                        className="home-hero-body"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2, ease: easeOut }}
                    >
                        {get("home.hero_body", "Rax the Edit is a curated resale destination featuring this season's most desirable fashion. We hand-pick the pieces worth hanging on the rack, so you don't have to search through thousands of listings.")}
                    </motion.p>
                    <motion.div
                        className="home-hero-actions"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.5 }}
                    >
                        <Link to="/shop" className="btn" data-testid="hero-shop-cta">
                            {get("home.hero_cta_primary", "Shop The Edit")}
                        </Link>
                        <Link to="/sell" className="btn btn-outline" data-testid="hero-sell-cta">
                            {get("home.hero_cta_secondary", "Sell With Us")}
                        </Link>
                    </motion.div>
                </div>
                <motion.div
                    className="home-hero-img"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.4, ease: easeOut }}
                >
                    <img
                        src={get("home.hero_image", "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1600&q=80")}
                        alt="RAX hero"
                    />
                </motion.div>
            </section>

            {/* Signature stripe */}
            <section className="stripe home-stripe">
                <div className="stripe-inner">
                    <span className="lockup-lg">RAX</span>
                    <div className="the-edit-lockup" style={{ marginTop: 6 }}>
                        <span>The Edit</span>
                    </div>
                    <span className="tagline">{get("home.stripe_tagline", "Only what earns the rack.")}</span>
                </div>
            </section>

            {/* EDITS BENTO */}
            <section className="section">
                <div className="section-head">
                    <div>
                        <div className="eyebrow">{get("home.edits_eyebrow", "Curated Collections")}</div>
                        <h2 className="h-display">{get("home.edits_title", "The Edits")}</h2>
                    </div>
                    <Link to="/edits" className="link-underline" data-testid="edits-view-all">View all</Link>
                </div>

                <div className="edits-grid" data-testid="edits-grid">
                    {edits.slice(0, 4).map((e, i) => (
                        <motion.div
                            key={e.slug}
                            className={`edit-card ${i === 0 ? "big" : "small"}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: i * 0.05, ease: easeOut }}
                        >
                            <Link to={`/shop?edit=${e.slug}`} aria-label={e.title} data-testid={`edit-card-${e.slug}`}>
                                <img src={e.image} alt={e.title} loading="lazy" />
                                <div className="edit-card-copy">
                                    <div className="eyebrow">The Edit</div>
                                    <h3 className="title">{e.title}</h3>
                                    <p className="blurb">{e.blurb}</p>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* HERO PIECES */}
            <section className="section" style={{ paddingTop: 0 }}>
                <div className="section-head">
                    <div>
                        <div className="eyebrow">{get("home.pieces_eyebrow", "Fresh in")}</div>
                        <h2 className="h-display">{get("home.pieces_title", "Pieces from the rack.")}</h2>
                    </div>
                    <Link to="/shop" className="link-underline" data-testid="products-view-all">View all</Link>
                </div>
                <div className="product-grid" data-testid="home-product-grid">
                    {products.slice(0, 8).map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* SELL WITH US BAND */}
            <div className="sell-band">
                <div className="img">
                    <img
                        src={get("home.sell_band_image", "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1600&q=80")}
                        alt="Curated wardrobe rack"
                    />
                </div>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 20 }}>Sell With Us</div>
                    <h2>{get("home.sell_band_title", "Have a piece worth hanging on the rack?")}</h2>
                    <p>{get("home.sell_band_body", "Consign the pieces you no longer wear to a small, considered audience. We handle the photography, styling, and the sale. You keep the majority.")}</p>
                    <Link to="/sell" className="btn" data-testid="sell-band-cta">{get("home.sell_band_cta", "Start a submission")}</Link>
                </div>
            </div>

            {/* TRUST STRIP */}
            <section className="trust-strip" data-testid="trust-strip">
                <div className="trust-strip-inner">
                    <div className="item">
                        <div className="eyebrow">{get("home.trust_1_label", "Authenticated")}</div>
                        <div className="title">{get("home.trust_1_body", "Every piece reviewed by our curators.")}</div>
                    </div>
                    <div className="item">
                        <div className="eyebrow">{get("home.trust_2_label", "Curated")}</div>
                        <div className="title">{get("home.trust_2_body", "Only what would hang on a friend's rack.")}</div>
                    </div>
                    <div className="item">
                        <div className="eyebrow">{get("home.trust_3_label", "Secure")}</div>
                        <div className="title">{get("home.trust_3_body", "Stripe checkout with buyer protection.")}</div>
                    </div>
                </div>
            </section>
        </div>
    );
}
